import { Link, useNavigate } from '@reach/router'
import { Alert, Avatar, Button, Col, Form, Input, Row } from 'antd'
import React, { createContext, useContext, useReducer, useState } from 'react'
import styled from 'styled-components'
import { Card, ErrorBanner } from './ui'
import { required, validateMessages } from '../common/validate'
import { getLoginSp, loginSubmit } from '../services'
import { useLoading } from '../common/hooks'
import { ROOT_PATH, TOKEN_SESSION_KEY, USER_SESSION_EKY } from '../common/config'
import { AvatarEnum } from './AvatarEnum'

enum LoginStep {
  EnterUsername,
  ConfirmPharse,
  EnterPassword,
}

const StyledLogin = styled.div`
  width: 486px;

  .card {
    background: transparent !important;
    box-shadow: none !important;
  }

  @media (max-width: 992px) {
    width: 100vw;
    .card {
      width: 486px;
      max-width: 80vw;
      margin: 0 auto;
    }
  }
  @media (max-width: 600px) {
    width: 100%;
    .card {
      width: 100%;
      margin: 0 auto;
      padding-left: 0;
      padding-right: 0;
    }
  }

  .title {
    font-size: 32px;
    font-weight: 500;
    text-transform: uppercase;
  }

  .login-head {
    margin-bottom: 46px;
  }

  .avatar {
    margin-bottom: 10px;
  }

  .ant-alert {
    margin-bottom: 20px;
    text-align: center;
  }

  .ant-form-item-label > label,
  .form-link {
    font-size: 16px;
  }

  .ant-form-item-label > label {
    font-weight: bold;
  }
`

const LoginStep1 = () => {
  const { loading, start, finish } = useLoading()
  const { dispatch } = useContext(LoginContext)
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)

  const onFinish = async (values: { username: string }) => {
    console.log(values)
    setErrMsg(undefined)
    start()
    try {
      const { image, lgnId, scrtPhr, name } = await getLoginSp({ usrId: values.username })
      console.log(image, lgnId, scrtPhr, name)
      finish()
      dispatch({ type: 'ApplyForm', payload: { image, lgnId, scrtPhr, name, username: values.username } })
      dispatch({ type: 'ChangeStep', payload: LoginStep.ConfirmPharse })
      setErrMsg(undefined)
    } catch (error) {
      setErrMsg(error.message)
      finish()
    }
  }
  return (
    <>
      <h3 className="text-3xl md:text-5xl text-center my-4 md:my-10 font-bold">Welcome!</h3>
      <Form layout="vertical" requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
        <Form.Item name="username" label="Username" rules={[required, { min: 8, max: 30, validateTrigger: 'blur' }]}>
          <Input size="large" placeholder="Enter Your Username" />
        </Form.Item>
        <Form.Item className="-mt-3">
          <Link className="form-link" to={`${ROOT_PATH}/forgot/username`}>
            Forgotten your Username?
          </Link>
        </Form.Item>

        <Button block size="large" type="primary" htmlType="submit" loading={loading}>
          Login
        </Button>

        {errMsg && <ErrorBanner>{errMsg}</ErrorBanner>}
      </Form>
    </>
  )
}

const LoginStep2: React.FC = () => {
  const { state, dispatch } = useContext(LoginContext)

  return (
    <>
      <h3 className="text-3xl md:text-5xl text-center my-4 md:my-10 font-bold">Hello {state.name}</h3>
      <Row align="middle" justify="center">
        <Col className="avatar">
          <Avatar size={64} src={AvatarEnum[Number(state.image)]} />
        </Col>
        <Col span={24}>
          <p className="text-center text-lg">please confirm your security phrase</p>
          <Alert message={state.scrtPhr} type="info" className="text-2xl" />
        </Col>
        <div className="flex justify-between w-full">
          <Button className="flex-1 mr-5" size="large" type="primary" danger onClick={() => dispatch({ type: 'ChangeStep', payload: LoginStep.EnterUsername })}>
            No
          </Button>
          <Button className="flex-1 ml-5" size="large" type="primary" onClick={() => dispatch({ type: 'ChangeStep', payload: LoginStep.EnterPassword })}>
            Yes
          </Button>
        </div>
      </Row>
    </>
  )
}

const LoginStep3: React.FC = () => {
  const { loading, start, finish } = useLoading()
  const { state, dispatch } = useContext(LoginContext)
  const navigate = useNavigate()
  const login = () => navigate(ROOT_PATH + '/dashboard/account', { replace: true })
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
  const onFinish = async (values: { password: string }) => {
    if (!values.password) return
    start()
    setErrMsg(undefined)
    try {
      const { token, lastLogonTime, sex, tel } = (await loginSubmit({ lgnId: state.lgnId, pwd: values.password })) || {}
      if (!token) {
        finish()
        return
      }
      sessionStorage.setItem(TOKEN_SESSION_KEY, token)
      sessionStorage.setItem(USER_SESSION_EKY, JSON.stringify({ name: state.name, avatar: state.image, lastLogonTime, username: state.username, sex, tel }))
      finish()
      login()
      setErrMsg(undefined)
    } catch (error) {
      finish()
      setErrMsg(error.message)
    }
  }
  return (
    <>
      <h3 className="text-3xl md:text-5xl text-center my-4 md:my-10 font-bold">Hello {state.name}</h3>
      <Form layout="vertical" requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
        <Form.Item label="Password" name="password" rules={[required]} help="">
          <Input.Password size="large" placeholder="Enter Your Password" />
        </Form.Item>
        <Form.Item>
          <Link className="form-link" to={`${ROOT_PATH}/forgot/password`}>
            Forgotten your password?
          </Link>
        </Form.Item>
        <div className="flex justify-between w-full">
          <Button className="flex-1 mr-5" size="large" disabled={loading} type="primary" danger onClick={() => dispatch({ type: 'ChangeStep', payload: LoginStep.ConfirmPharse })}>
            Back
          </Button>
          <Button className="flex-1 ml-5" size="large" type="primary" htmlType="submit">
            Login
          </Button>
        </div>
      </Form>
      {errMsg && (
        <div style={{ marginTop: '1em' }}>
          <ErrorBanner>{errMsg}</ErrorBanner>
        </div>
      )}
    </>
  )
}

const Steps = {
  [LoginStep.EnterUsername]: LoginStep1,
  [LoginStep.ConfirmPharse]: LoginStep2,
  [LoginStep.EnterPassword]: LoginStep3,
}
interface LoginState {
  step: LoginStep
  username: string
  phrase: string
  image: string
  lgnId: string
  scrtPhr: string
  name: string
  lastLogonTime: string
}

type LoginAction = { type: 'ChangeStep'; payload: LoginStep } | { type: 'ApplyForm'; payload: Partial<LoginState> }

const LoginContext = createContext({ state: {} as LoginState, dispatch: {} as React.Dispatch<LoginAction> })
const useLogin = () => {
  const initialState: LoginState = {
    step: LoginStep.EnterUsername,
    username: '',
    phrase: '',
    image: '',
    lgnId: '',
    scrtPhr: '',
    name: '',
    lastLogonTime: '',
  }
  const reducer = (state: LoginState, action: LoginAction): LoginState => {
    switch (action.type) {
      case 'ChangeStep':
        return { ...state, step: action.payload }
      case 'ApplyForm':
        return { ...state, ...action.payload }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  return {
    state,
    dispatch,
  }
}

const LoginProvider: React.FC = (props) => {
  const { state, dispatch } = useLogin()
  return <LoginContext.Provider value={{ state, dispatch }}>{props.children}</LoginContext.Provider>
}

const LoginForm: React.FC = () => {
  const { state } = useContext(LoginContext)
  const LoginStep = Steps[state.step]
  return (
    <StyledLogin>
      <Card>
        {/* <Row justify="space-between" className="login-head">
          <Col>
            <span className="title">LOGIN</span>
          </Col>
          <Col>
            <Brand size="mini" />
          </Col>
        </Row> */}
        <LoginStep />
      </Card>
    </StyledLogin>
  )
}

export const Login: React.FC = () => {
  return (
    <LoginProvider>
      <LoginForm />
    </LoginProvider>
  )
}
